import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  MoreVertical,
  RotateCcw,
  Check,
  Trash2,
  CircleCheck,
  CircleX,
  CircleHelp,
  CornerDownRight,
  Sparkles,
} from "lucide-react";
import type { Quiz } from "@/features/quizzes/types";
import type { QuizQuestionData, QuestionResult } from "@/types/quizzes";
import type { QuizHelperRequest } from "@/types/quizHelpers";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { FreeResponseQuestion } from "./FreeResponseQuestion";
import { toast } from "sonner";
import { incrementUsage, checkUsageLimit, UsageLimitExceededError } from "@/hooks/useUsageLimits";
import { useUsageLimitContext } from "@/context/UsageLimitContext";
import { trackQuizCompleted } from "@/lib/analytics";

interface QuizTakingViewProps {
  quiz: Quiz;
  onBack: () => void;
  onRestart: () => void;
  onGenerateMore: () => void;
  onHelperRequest?: (req: QuizHelperRequest) => void;
}

export const QuizTakingView: React.FC<QuizTakingViewProps> = ({
  quiz,
  onBack,
  onRestart,
  onGenerateMore,
  onHelperRequest,
}) => {
  const { user } = useAuth();
  
  // Get limit error context - wrapped in try-catch for when context isn't available
  let showLimitError: ((error: UsageLimitExceededError) => void) | null = null;
  try {
    const context = useUsageLimitContext();
    showLimitError = context.showLimitError;
  } catch {
    // Context not available
  }
  
  const [questions, setQuestions] = useState<QuizQuestionData[]>([]);
  const [attemptId, setAttemptId] = useState<string | null>(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentState, setCurrentState] = useState<QuestionResult>("unanswered");
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);

  const [correctCount, setCorrectCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Free-response specific state
  const [freeResponseAnswer, setFreeResponseAnswer] = useState("");
  const [isGradingFreeResponse, setIsGradingFreeResponse] = useState(false);

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;
  
  const progressPercent = useMemo(() => {
    if (!totalQuestions) return 0;
    return Math.round((correctCount / totalQuestions) * 100);
  }, [correctCount, totalQuestions]);

  // Load questions + create attempt on mount
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (!user) return;
      setIsLoading(true);

      // 1) Load questions
      const { data: qs, error: qErr } = await supabase
        .from("quiz_questions")
        .select("*")
        .eq("quiz_id", quiz.id)
        .order("question_index", { ascending: true });

      if (qErr) {
        console.error("Failed to load questions", qErr);
        setIsLoading(false);
        return;
      }

      if (cancelled) return;

      const castQuestions = (qs ?? []).map((q) => ({
        id: q.id,
        quiz_id: q.quiz_id,
        prompt: q.prompt,
        question_type: q.question_type as QuizQuestionData["question_type"],
        options: q.options as string[] | null,
        correct_answer: q.correct_answer as QuizQuestionData["correct_answer"],
        explanation: q.explanation,
        question_index: q.question_index,
      }));
      setQuestions(castQuestions);

      // 2) Create attempt
      const { data: attempt, error: aErr } = await supabase
        .from("quiz_attempts")
        .insert({
          quiz_id: quiz.id,
          user_id: user.id,
          total_questions: castQuestions.length,
          started_at: new Date().toISOString(),
        })
        .select("id")
        .single();

      if (aErr) {
        console.error("Failed to create attempt", aErr);
        setIsLoading(false);
        return;
      }

      if (cancelled) return;

      setAttemptId(attempt?.id ?? null);
      setIsLoading(false);
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [quiz.id, user]);

  const resetCurrentQuestionUI = () => {
    setCurrentState("unanswered");
    setSelectedOptionIndex(null);
    setShowExplanation(false);
    setFreeResponseAnswer("");
    setIsGradingFreeResponse(false);
  };

  const getCorrectOptionIndex = (question: QuizQuestionData): number | null => {
    if (!question.correct_answer) return null;
    
    // Check for correct_index first (MCQ format: {correct_index: 0, type: "mcq"})
    if (typeof question.correct_answer.correct_index === "number") {
      return question.correct_answer.correct_index;
    }
    
    // Legacy: check for index field
    if (typeof question.correct_answer.index === "number") {
      return question.correct_answer.index;
    }
    
    // For true/false questions: {value: true/false, type: "true_false"}
    if (question.correct_answer.value !== undefined && question.options) {
      const valueStr = String(question.correct_answer.value).toLowerCase();
      const idx = question.options.findIndex(
        (opt) => opt.toLowerCase() === valueStr
      );
      return idx >= 0 ? idx : null;
    }
    
    return null;
  };

  const handleSelectOption = (optionIndex: number) => {
    if (!currentQuestion || currentState !== "unanswered") return;

    setSelectedOptionIndex(optionIndex);

    const correctIndex = getCorrectOptionIndex(currentQuestion);
    const isCorrect = correctIndex !== null && correctIndex === optionIndex;

    const nextState: QuestionResult = isCorrect ? "correct" : "incorrect";
    setCurrentState(nextState);
    setShowExplanation(true);
  };

  const handleDontKnow = () => {
    if (!currentQuestion || currentState !== "unanswered") return;

    setSelectedOptionIndex(null);
    setCurrentState("skipped");
    setShowExplanation(true);
  };

  // Free-response submission with AI grading
  const handleFreeResponseSubmit = async (userAnswer: string) => {
    if (!currentQuestion || currentState !== "unanswered") return;
    
    setFreeResponseAnswer(userAnswer);
    setIsGradingFreeResponse(true);

    try {
      // Extract correct answer and keywords from the question
      const correctAnswerObj = currentQuestion.correct_answer as { 
        value?: string; 
        keywords?: string[];
        expected_keywords?: string[];
      } | null;
      
      const correctAnswer = correctAnswerObj?.value ?? "";
      const keywords = correctAnswerObj?.keywords || correctAnswerObj?.expected_keywords || [];
      
      // Call the AI grading edge function
      const response = await supabase.functions.invoke("grade-free-response", {
        body: {
          userAnswer,
          correctAnswer,
          keywords,
          questionPrompt: currentQuestion.prompt,
        },
      });

      if (response.error) {
        console.error("Grading error:", response.error);
        toast.error("Failed to grade answer, please try again");
        setIsGradingFreeResponse(false);
        return;
      }

      const { verdict } = response.data as { verdict: "correct" | "partial" | "incorrect" };
      
      // Partial is treated as correct for progress purposes
      const isCorrect = verdict === "correct" || verdict === "partial";
      
      setCurrentState(isCorrect ? "correct" : "incorrect");
      setShowExplanation(true);
      
      if (verdict === "partial") {
        toast.info("Your answer was partially correct!");
      }
    } catch (error) {
      console.error("Grading failed:", error);
      toast.error("Failed to grade answer");
    } finally {
      setIsGradingFreeResponse(false);
    }
  };

  const handleRetry = () => {
    if (!currentQuestion || currentState === "unanswered") return;
    resetCurrentQuestionUI();
  };

  // Persist answer when user presses Next
  const persistAnswerForCurrentQuestion = async (): Promise<boolean> => {
    if (!attemptId || !currentQuestion || currentState === "unanswered") return true;

    try {
      // Check usage limit before persisting answer
      const check = await checkUsageLimit("quiz_answers");
      if (!check.canProceed) {
        if (showLimitError) {
          showLimitError(new UsageLimitExceededError("quiz_answers", check.used, check.limit));
        } else {
          toast.error("You've reached your daily quiz answer limit. Upgrade to continue.");
        }
        return false;
      }

      // Build user_answer based on question type
      const userAnswerPayload = currentQuestion.question_type === "free_response"
        ? { text: freeResponseAnswer }
        : { selected_index: selectedOptionIndex };

      await supabase.from("quiz_attempt_answers").insert({
        attempt_id: attemptId,
        question_id: currentQuestion.id,
        question_index: currentIndex,
        is_correct: currentState === "correct",
        user_answer: userAnswerPayload,
      });

      // Track usage for quiz answer
      await incrementUsage("quiz_answers");
      return true;
    } catch (err) {
      console.error("Failed to persist quiz answer", err);
      return true; // Allow proceeding on error
    }
  };

  const handleNext = async () => {
    if (!currentQuestion || currentState === "unanswered") return;

    // 1) Save to DB - if blocked by usage limit, don't proceed
    const saved = await persistAnswerForCurrentQuestion();
    if (!saved) return;

    // 2) Update progress (only when correct)
    if (currentState === "correct") {
      setCorrectCount((prev) => prev + 1);
    }

    // 3) Last question → complete flow
    if (currentIndex === totalQuestions - 1) {
      setIsComplete(true);

      // Mark attempt as completed in Supabase
      if (attemptId) {
        const finalCorrect = currentState === "correct" ? correctCount + 1 : correctCount;
        const finalScore = totalQuestions > 0 ? Math.round((finalCorrect / totalQuestions) * 100) : 0;

        supabase
          .from("quiz_attempts")
          .update({
            completed_at: new Date().toISOString(),
            total_correct: finalCorrect,
            score: finalScore,
          })
          .eq("id", attemptId)
          .then(({ error }) => {
            if (error) console.error("Failed to complete attempt", error);
          });

        // Also update the quiz's last attempt info
        supabase
          .from("quizzes")
          .update({
            last_attempted_at: new Date().toISOString(),
            last_score: finalScore,
            last_total_correct: finalCorrect,
            last_total_questions: totalQuestions,
          })
          .eq("id", quiz.id)
          .then(({ error }) => {
            if (error) console.error("Failed to update quiz stats", error);
          });

        trackQuizCompleted(quiz.id, finalScore, totalQuestions, finalCorrect);
      }

      return;
    }

    // 4) Move to next question
    setCurrentIndex((prev) => prev + 1);
    resetCurrentQuestionUI();
  };

  const handleRemoveQuestion = async () => {
    if (!currentQuestion) return;

    const confirm = window.confirm("Remove this question from the quiz?");
    if (!confirm) return;

    try {
      await supabase
        .from("quiz_questions")
        .delete()
        .eq("id", currentQuestion.id);

      setQuestions((prev) => prev.filter((q) => q.id !== currentQuestion.id));

      // If we removed the last question, adjust index
      if (currentIndex >= totalQuestions - 1) {
        setCurrentIndex(Math.max(0, currentIndex - 1));
      }
      resetCurrentQuestionUI();
    } catch (err) {
      console.error("Failed to delete question", err);
    }
  };

  const handleRestart = () => {
    onRestart();
  };

  // Helper styles for option buttons
  const getOptionClasses = (optionIndex: number): string => {
    if (!currentQuestion) return "";

    const correctIndex = getCorrectOptionIndex(currentQuestion);
    const baseClasses = "w-full rounded-2xl p-3 text-left flex items-start gap-2 transition-all";

    // Default unanswered state
    if (currentState === "unanswered") {
      return `${baseClasses} border border-border hover:bg-accent/50 hover:border-primary/30`;
    }

    // CORRECT state - user selected the correct answer
    if (currentState === "correct") {
      if (correctIndex === optionIndex) {
        // The correct option that was selected - solid green
        return `${baseClasses} border-2 border-correct bg-correct/10`;
      }
      // Other options when correct - dimmed
      return `${baseClasses} border border-border opacity-50`;
    }

    // INCORRECT state - user selected wrong answer
    if (currentState === "incorrect") {
      if (selectedOptionIndex === optionIndex) {
        // The wrong option that was selected - solid red
        return `${baseClasses} border-2 border-incorrect bg-incorrect/10`;
      }
      if (correctIndex === optionIndex) {
        // Show the correct answer with dashed green border
        return `${baseClasses} border-2 border-dashed border-correct`;
      }
      // Other options - dimmed
      return `${baseClasses} border border-border opacity-50`;
    }

    // SKIPPED state - user clicked "Don't know"
    if (currentState === "skipped") {
      if (correctIndex === optionIndex) {
        // Show the correct answer with dashed yellow border
        return `${baseClasses} border-2 border-dashed border-warning bg-warning/10`;
      }
      // Other options - dimmed
      return `${baseClasses} border border-border opacity-50`;
    }

    return `${baseClasses} border border-border`;
  };

  const letterForIndex = (index: number) =>
    String.fromCharCode("A".charCodeAt(0) + index);

  // Completion Screen
  if (isComplete) {
    return (
      <QuizCompletionScreen
        quizTitle={quiz.title}
        onBack={onBack}
        onRestart={handleRestart}
        onGenerateMore={onGenerateMore}
      />
    );
  }

  if (isLoading || !currentQuestion) {
    return (
      <div className="flex h-[320px] items-center justify-center text-sm text-muted-foreground">
        Loading quiz…
      </div>
    );
  }

  const correctIndex = getCorrectOptionIndex(currentQuestion);

  return (
    <div className="relative flex h-full flex-col">
      {/* Top nav: Back + Ellipsis */}
      <div className="flex w-full items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="mb-2 flex w-fit items-center gap-2 rounded-lg px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm">Back</span>
        </button>

        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="flex flex-row items-center gap-3 px-1 py-2">
        <span className="text-sm font-medium text-muted-foreground">
          {correctCount}
        </span>
        <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-primary/5 dark:bg-primary/10">
          <div
            className="h-full rounded-full bg-correct transition-all duration-300"
            style={{
              width: `${progressPercent}%`,
            }}
          />
        </div>
        <span className="text-sm font-medium text-muted-foreground">
          {totalQuestions}
        </span>
      </div>

      {/* Question + options */}
      <div className="flex-1 overflow-y-auto px-1 py-2">
        {/* Free Response Question */}
        {currentQuestion.question_type === "free_response" ? (
          <FreeResponseQuestion
            question={{
              id: currentQuestion.id,
              prompt: currentQuestion.prompt,
              correct_answer: currentQuestion.correct_answer as { value?: string } | null,
              explanation: currentQuestion.explanation,
            }}
            state={currentState}
            onSubmit={handleFreeResponseSubmit}
            onDontKnow={handleDontKnow}
            onRemove={handleRemoveQuestion}
            isGrading={isGradingFreeResponse}
          />
        ) : (
          <>
            {/* Question text + trash */}
            <div className="mb-4 flex items-start justify-between gap-2">
              <h2 className="text-base font-normal leading-7">
                {currentQuestion.prompt}
              </h2>
              <button
                type="button"
                onClick={handleRemoveQuestion}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            {/* Options (MCQ / True-False) */}
            {Array.isArray(currentQuestion.options) && (
              <div className="space-y-2">
                {currentQuestion.options.map((option, idx) => (
                  <button
                    key={idx}
                    type="button"
                    disabled={currentState !== "unanswered"}
                    onClick={() => handleSelectOption(idx)}
                    className={getOptionClasses(idx)}
                  >
                    <span className="pt-0.5 text-sm font-semibold text-muted-foreground">
                      {letterForIndex(idx)}.
                    </span>
                    <span className="flex-1 text-base leading-7">
                      {option}
                    </span>

                    {/* Icon on correct / wrong options */}
                    {currentState !== "unanswered" && correctIndex === idx && (
                      <div className="ml-auto flex items-center self-center">
                        <Check className="h-5 w-5 text-correct" />
                      </div>
                    )}

                    {currentState === "incorrect" &&
                      selectedOptionIndex === idx &&
                      correctIndex !== idx && (
                        <div className="ml-auto flex items-center self-center">
                          <CircleX className="h-5 w-5 text-incorrect" />
                        </div>
                      )}
                  </button>
                ))}
              </div>
            )}

            {/* Feedback explanation */}
            {showExplanation && currentQuestion.explanation && (
              <div
                className={cn(
                  "mt-4 rounded-lg p-4 text-sm leading-normal",
                  currentState === "correct" &&
                    "bg-correct/10 text-correct",
                  currentState === "incorrect" &&
                    "bg-incorrect/10 text-incorrect",
                  currentState === "skipped" &&
                    "bg-warning/10 text-warning"
                )}
              >
                <div className="mb-2 flex items-center gap-2 font-medium">
                  {currentState === "correct" && (
                    <>
                      <CircleCheck className="h-5 w-5" />
                      <span>Correct</span>
                    </>
                  )}
                  {currentState === "incorrect" && (
                    <>
                      <CircleX className="h-5 w-5" />
                      <span>Incorrect</span>
                    </>
                  )}
                  {currentState === "skipped" && (
                    <>
                      <CircleHelp className="h-5 w-5" />
                      <span>Don't know</span>
                    </>
                  )}
                </div>
                <p>{currentQuestion.explanation}</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Controls row */}
      <div className="mt-3 px-1">
        <div className="flex justify-between">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleRetry}
              disabled={currentState === "unanswered"}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-input bg-background text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RotateCcw className="h-4 w-4" />
            </button>

            <button
              type="button"
              disabled
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-input bg-background text-sm font-medium text-muted-foreground opacity-40"
            >
              <Check className="h-4 w-4" />
            </button>
          </div>

          <div className="flex gap-2">
            {/* Don't know button - hidden for free response since it's in the component */}
            {currentQuestion.question_type !== "free_response" && (
              <button
                type="button"
                onClick={handleDontKnow}
                disabled={currentState !== "unanswered"}
                className="inline-flex items-center justify-center rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50"
              >
                Don't know
              </button>
            )}
            <button
              type="button"
              onClick={handleNext}
              disabled={currentState === "unanswered"}
              className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="my-4 h-px w-full bg-border" />

      {/* Hint / Walkthrough / Simple cards */}
      <div className="flex flex-row gap-3">
        <QuizHelperCard 
          label="Give me a hint" 
          onClick={() => onHelperRequest?.({
            type: "hint",
            quizId: quiz.id,
            questionId: currentQuestion.id,
            questionPrompt: currentQuestion.prompt,
          })}
        />
        <QuizHelperCard 
          label="Walk me through it" 
          onClick={() => onHelperRequest?.({
            type: "walkthrough",
            quizId: quiz.id,
            questionId: currentQuestion.id,
            questionPrompt: currentQuestion.prompt,
          })}
        />
        <QuizHelperCard 
          label="Keep it simple" 
          className="hidden md:flex"
          onClick={() => onHelperRequest?.({
            type: "simplify",
            quizId: quiz.id,
            questionId: currentQuestion.id,
            questionPrompt: currentQuestion.prompt,
          })}
        />
      </div>
    </div>
  );
};

interface QuizHelperCardProps {
  label: string;
  onClick?: () => void;
  className?: string;
}

const QuizHelperCard: React.FC<QuizHelperCardProps> = ({ label, onClick, className }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex h-full w-full cursor-pointer items-center rounded-2xl border p-2 text-sm leading-relaxed transition-all duration-200 hover:bg-accent/50",
      className
    )}
  >
    <CornerDownRight className="ml-1 h-5 w-5 flex-shrink-0 text-muted-foreground" />
    <span className="ml-2 line-clamp-2 text-left text-muted-foreground">{label}</span>
  </button>
);

interface QuizCompletionScreenProps {
  quizTitle: string;
  onBack: () => void;
  onRestart: () => void;
  onGenerateMore: () => void;
}

const QuizCompletionScreen: React.FC<QuizCompletionScreenProps> = ({
  quizTitle,
  onBack,
  onRestart,
  onGenerateMore,
}) => {
  return (
    <div className="relative flex min-h-[320px] flex-col items-center justify-center space-y-6 py-8">
      <button
        type="button"
        onClick={onBack}
        className="absolute left-0 top-0 flex items-center gap-2 rounded-lg px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back</span>
      </button>

      <div className="flex h-24 w-24 items-center justify-center">
        <CircleCheck className="h-24 w-24 text-correct" strokeWidth={1.5} />
      </div>

      <div className="space-y-1 text-center">
        <h2 className="text-lg font-semibold">
          Quiz: "{quizTitle.length > 40 ? quizTitle.slice(0, 40) + "..." : quizTitle}"
        </h2>
        <p className="text-sm text-muted-foreground">
          You've completed this quiz. Continue to the next one!
        </p>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onRestart}
          className="inline-flex items-center justify-center rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          Restart
        </button>
        <button
          type="button"
          onClick={onGenerateMore}
          className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Sparkles className="mr-2 h-4 w-4" />
          Generate More
        </button>
      </div>
    </div>
  );
};
